import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import toast from "react-hot-toast";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { 
  MessageCircle, X, Send, Loader2, Bot, User, 
  Sparkles, HelpCircle, ChevronDown, Trash2,
  DollarSign, Gift, Shield, Gamepad2, Crown, Map, ScrollText,
  UserCheck, PhoneCall, ArrowLeft
} from "lucide-react";

const ICON_MAP = {
  dollar: DollarSign,
  gift: Gift,
  shield: Shield,
  sparkles: Sparkles,
  gamepad: Gamepad2,
  crown: Crown,
  map: Map,
  scroll: ScrollText,
};

/**
 * SupportChatWidget - Floating AI Support Chatbot
 * 
 * Features:
 * - GPT-5.2 powered responses
 * - Session persistence
 * - Quick answer suggestions
 * - Works for both guests and authenticated users
 */

const BOT_AVATAR = "/bot-mascot.jpg";

const SupportChatWidget = () => {
  const { user, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [quickAnswers, setQuickAnswers] = useState([]);
  const [showQuickAnswers, setShowQuickAnswers] = useState(true);
  const [liveAgentMode, setLiveAgentMode] = useState(false);
  const [liveSession, setLiveSession] = useState(null);
  const [liveMessages, setLiveMessages] = useState([]);
  const [liveInput, setLiveInput] = useState("");
  const [requestingAgent, setRequestingAgent] = useState(false);
  const [liveHours, setLiveHours] = useState({ closed: false, next_open: "", message: "" });
  const messagesEndRef = useRef(null);
  const liveEndRef = useRef(null);
  const inputRef = useRef(null);
  const livePollingRef = useRef(null);

  // Load session from localStorage
  useEffect(() => {
    const savedSession = localStorage.getItem("support_session_id");
    if (savedSession) {
      setSessionId(savedSession);
      loadChatHistory(savedSession);
    }
    loadQuickAnswers();
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const loadQuickAnswers = async () => {
    try {
      const res = await axios.get(`${API}/support/quick-answers`);
      setQuickAnswers(res.data.quick_answers || []);
    } catch (e) {
      console.error("Failed to load quick answers:", e);
    }
  };

  // Check for existing live agent session on mount
  useEffect(() => {
    if (user && token) {
      checkLiveAgentStatus();
    }
  }, [user, token]);

  const checkLiveAgentStatus = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/support/live-agent/status`, { headers });
      if (res.data.has_session) {
        setLiveAgentMode(true);
        setLiveSession(res.data);
        setLiveMessages(res.data.messages || []);
      }
    } catch (e) { /* ignore */ }
  };

  // Poll for live agent messages
  useEffect(() => {
    if (liveAgentMode && liveSession?.session_id && isOpen) {
      livePollingRef.current = setInterval(async () => {
        try {
          const headers = { Authorization: `Bearer ${token}` };
          const res = await axios.get(`${API}/support/live-agent/status`, { headers });
          if (res.data.has_session) {
            setLiveMessages(res.data.messages || []);
            setLiveSession(res.data);
          } else {
            setLiveAgentMode(false);
            setLiveSession(null);
            setLiveMessages([]);
            clearInterval(livePollingRef.current);
          }
        } catch (e) { /* ignore */ }
      }, 3000);
      return () => clearInterval(livePollingRef.current);
    }
  }, [liveAgentMode, liveSession?.session_id, isOpen, token]);

  // Scroll live messages
  useEffect(() => {
    liveEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [liveMessages]);

  const requestLiveAgent = async () => {
    if (!user || !token) return;
    setRequestingAgent(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/support/live-agent/request`, {}, { headers });
      setLiveAgentMode(true);
      setLiveSession({ session_id: res.data.session_id, status: res.data.status, messages: [] });
      setLiveMessages([]);
    } catch (e) {
      // Business-hours gate — surface the exact offline copy the backend sent.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 503 && detail?.code === "LIVE_CHAT_OFFLINE") {
        toast(detail.message, { icon: "🕒", duration: 6000 });
      } else {
        console.error("Failed to request live agent:", e);
      }
    }
    setRequestingAgent(false);
  };

  // Poll live-agent hours every 5 min so the weekend banner updates in real time
  useEffect(() => {
    let cancelled = false;
    const fetchHours = async () => {
      try {
        const res = await axios.get(`${API}/support/live-agent/hours`);
        if (!cancelled) setLiveHours(res.data || { closed: false });
      } catch (_) { /* ignore */ }
    };
    fetchHours();
    const t = setInterval(fetchHours, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const sendLiveMessage = async () => {
    if (!liveInput.trim() || !token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/support/live-agent/message`, { message: liveInput }, { headers });
      setLiveInput("");
      // Immediately fetch updated messages
      const res = await axios.get(`${API}/support/live-agent/status`, { headers });
      if (res.data.has_session) setLiveMessages(res.data.messages || []);
    } catch (e) { console.error(e); }
  };

  const closeLiveSession = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/support/live-agent/close`, {}, { headers });
    } catch (e) { /* ignore */ }
    setLiveAgentMode(false);
    setLiveSession(null);
    setLiveMessages([]);
  };

  const loadChatHistory = async (sid) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/support/history/${sid}`, { headers });
      if (res.data.messages && res.data.messages.length > 0) {
        setMessages(res.data.messages);
        setShowQuickAnswers(false);
      }
    } catch (e) {
      console.error("Failed to load chat history:", e);
    }
  };

  const sendMessage = async (messageText = inputValue) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      role: "user",
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);
    setShowQuickAnswers(false);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(
        `${API}/support/chat`,
        { message: messageText, session_id: sessionId },
        { headers }
      );

      const newSessionId = res.data.session_id;
      if (newSessionId && newSessionId !== sessionId) {
        setSessionId(newSessionId);
        localStorage.setItem("support_session_id", newSessionId);
      }

      const assistantMessage = {
        role: "assistant",
        content: res.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Auto-switch to live agent mode if AI escalated
      if (res.data.escalated) {
        setTimeout(() => checkLiveAgentStatus(), 1000);
      }
    } catch (e) {
      console.error("Failed to send message:", e);
      const errorMessage = {
        role: "assistant",
        content: "Sorry, I'm having trouble connecting right now. Please try again or email us at support@jackroyals.com for help. 🙏",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.delete(`${API}/support/history/${sessionId}`, { headers });
      } catch (e) {
        console.error("Failed to clear history:", e);
      }
    }
    setMessages([]);
    setShowQuickAnswers(true);
    localStorage.removeItem("support_session_id");
    setSessionId(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleQuickAnswer = (text) => {
    sendMessage(text);
  };

  // ── Live Chat business-hours gate (2026-Feb: 8 AM – 11:59 PM CT daily) ──
  const [liveSchedule, setLiveSchedule] = useState({ is_open: true, message: null });
  useEffect(() => {
    let cancelled = false;
    const fetchSchedule = async () => {
      try {
        const res = await axios.get(`${API}/support/live-agent/schedule`);
        if (!cancelled) setLiveSchedule(res.data || { is_open: true });
      } catch { /* ignore */ }
    };
    fetchSchedule();
    const t = setInterval(fetchSchedule, 60 * 1000); // recheck every minute
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  const LIVE_OFFLINE_MSG = "Live Chat is currently offline. We are open daily from 8 AM to 11:59 PM Central Time.";

  // ── Admin-initiated message notification (2026-Feb owner directive) ──
  // Polls a lightweight endpoint every 20s. When the newest admin message
  // is unread, we fire a one-time toast (per session-id + timestamp) and
  // paint a red pulse dot on the floating button so the player notices.
  const [adminUnread, setAdminUnread] = useState(false);
  const lastAdminNotifiedRef = useRef(null);
  const lastDmNotifiedRef = useRef(null);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const poll = async () => {
      try {
        // Live Chat admin-initiated unread (separate system)
        const r = await axios.get(`${API}/support/live-agent/user/unread`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const liveUnread = !!r.data?.has_unread;
        if (liveUnread) {
          const key = `${r.data.session_id}|${r.data.last_admin_at || ""}`;
          if (lastAdminNotifiedRef.current !== key) {
            lastAdminNotifiedRef.current = key;
            const who = r.data.admin_username || "JackRoyals Support";
            toast(`💬 ${who} sent you a message — open support chat to read it`, {
              icon: "🔔", duration: 8000,
            });
          }
        }
        // Direct Messages (separate hub, `admin_direct_messages`)
        const d = await axios.get(`${API}/user/dm/unread`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: null }));
        const dmUnread = !!d.data?.has_unread;
        if (dmUnread) {
          const key = d.data?.latest?.id || d.data?.latest?.timestamp || "";
          if (lastDmNotifiedRef.current !== key) {
            lastDmNotifiedRef.current = key;
            const who = d.data.admin_username || "JackRoyals Support";
            const preview = (d.data?.latest?.content || "").slice(0, 80);
            toast(`✉️ New message from ${who}: ${preview}${preview.length >= 80 ? "…" : ""}`, {
              icon: "📬", duration: 9000,
            });
          }
        }
        setAdminUnread(liveUnread || dmUnread);
      } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, 20 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token]);
  // Mark read whenever the widget opens.
  useEffect(() => {
    if (isOpen && adminUnread && token) {
      axios.post(`${API}/support/live-agent/user/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(() => setAdminUnread(false)).catch(() => {});
      axios.post(`${API}/user/dm/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
  }, [isOpen, adminUnread, token]);

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => {
          if (!liveSchedule.is_open) {
            toast(liveSchedule.message || LIVE_OFFLINE_MSG, { icon: "🕒", duration: 5000 });
            return;
          }
          setIsOpen(!isOpen);
        }}
        title={liveSchedule.is_open ? undefined : (liveSchedule.message || LIVE_OFFLINE_MSG)}
        aria-disabled={!liveSchedule.is_open}
        className={`fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-50 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 overflow-hidden ${
          !liveSchedule.is_open
            ? "h-11 px-4 gap-2 bg-zinc-800 border border-zinc-600 text-zinc-300 cursor-not-allowed"
            : `w-14 h-14 ${isOpen
                ? "bg-zinc-800 hover:bg-zinc-700"
                : "ring-2 ring-amber-500/60 hover:ring-amber-400 animate-pulse"}`
        }`}
        data-testid={liveSchedule.is_open ? "support-chat-toggle" : "support-chat-offline"}
      >
        {!liveSchedule.is_open ? (
          <>
            <span className="w-2 h-2 rounded-full bg-zinc-500" />
            <span className="text-xs font-bold uppercase tracking-wider">Support Offline</span>
          </>
        ) : isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <>
            <img src={BOT_AVATAR} alt="Support" className="w-full h-full object-cover" />
            {adminUnread && (
              <span
                className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-rose-500 border-2 border-black animate-ping"
                data-testid="admin-unread-indicator"
              />
            )}
            {adminUnread && (
              <span className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-rose-500 border-2 border-black" />
            )}
          </>
        )}
      </button>

      {/* Chat Widget */}
      {isOpen && (
        <div 
          className="fixed bottom-36 sm:bottom-24 right-4 sm:right-6 z-50 w-[380px] max-w-[calc(100vw-32px)] h-[520px] max-h-[calc(100vh-160px)] bg-[#0a0a0a] rounded-2xl shadow-2xl border border-white/10 flex flex-col overflow-hidden"
          data-testid="support-chat-widget"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b border-white/10 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                  title="Minimize chat"
                  data-testid="support-chat-back"
                >
                  <ChevronDown className="w-5 h-5 text-zinc-400 hover:text-white" />
                </button>
                <img src={BOT_AVATAR} alt="Support Bot" className="w-10 h-10 rounded-full object-cover ring-2 ring-amber-500/40" />
                <div>
                  <h3 className="text-white font-bold text-sm">JackRoyals Support</h3>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="text-emerald-400 text-xs">Online</span>
                  </div>
                </div>
              </div>
              <button 
                onClick={clearChat}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Clear chat"
              >
                <Trash2 className="w-4 h-4 text-zinc-400 hover:text-white" />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Welcome Message */}
            {messages.length === 0 && (
              <div className="text-center py-6">
                <img src={BOT_AVATAR} alt="Support Bot" className="w-16 h-16 rounded-full object-cover ring-2 ring-amber-500/30 mx-auto mb-4" />
                <h4 className="text-white font-bold mb-2">Hi{user ? `, ${user.username}` : ""}!</h4>
                <p className="text-zinc-400 text-sm">
                  I'm your AI support assistant. How can I help you today?
                </p>
              </div>
            )}

            {/* Quick Answers */}
            {showQuickAnswers && quickAnswers.length > 0 && messages.length === 0 && (
              <div className="space-y-2">
                <p className="text-zinc-500 text-xs uppercase tracking-wider">Quick Questions</p>
                <div className="grid grid-cols-2 gap-2">
                  {quickAnswers.map((qa) => {
                    const IconComp = ICON_MAP[qa.icon] || HelpCircle;
                    return (
                      <button
                        key={qa.id}
                        onClick={() => handleQuickAnswer(qa.text)}
                        className="text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg border border-white/5 hover:border-amber-500/30 transition-all flex items-center gap-2"
                        data-testid={`quick-answer-${qa.id}`}
                      >
                        <IconComp className="w-4 h-4 text-amber-400 flex-shrink-0" />
                        <span className="text-white text-xs">{qa.text}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Chat Messages */}
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "assistant" && (
                  <img src={BOT_AVATAR} alt="Bot" className="w-8 h-8 rounded-full object-cover flex-shrink-0 ring-1 ring-amber-500/30" />
                )}
                <div
                  className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                    msg.role === "user"
                      ? "bg-amber-500 text-black rounded-br-md"
                      : "bg-white/10 text-white rounded-bl-md"
                  }`}
                >
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
                {msg.role === "user" && (
                  <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}

            {/* Loading Indicator */}
            {isLoading && (
              <div className="flex gap-3 justify-start">
                <img src={BOT_AVATAR} alt="Bot" className="w-8 h-8 rounded-full object-cover flex-shrink-0 ring-1 ring-amber-500/30" />
                <div className="bg-white/10 rounded-2xl rounded-bl-md p-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
                    <span className="text-zinc-400 text-sm">Thinking...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-white/10 p-4 bg-black/50">
            {liveAgentMode ? (
              /* Live Agent Chat Panel */
              <div>
                <div className="mb-3 max-h-[280px] overflow-y-auto space-y-2 bg-white/5 rounded-xl p-3">
                  {liveSession?.status === "waiting" && liveMessages.length === 0 && (
                    <div className="text-center py-4">
                      <Loader2 className="w-6 h-6 text-amber-400 animate-spin mx-auto mb-2" />
                      <p className="text-amber-400 text-sm font-medium">Waiting for an agent...</p>
                      <p className="text-zinc-500 text-xs mt-1">An agent has been notified and will join shortly.</p>
                    </div>
                  )}
                  {liveMessages.map((msg, i) => (
                    <div key={i} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                      {msg.role === "admin" && (
                        <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                          <UserCheck className="w-3 h-3 text-emerald-400" />
                        </div>
                      )}
                      {msg.role === "system" && (
                        <div className="w-full text-center">
                          <span className="text-zinc-500 text-[10px] italic">{msg.content}</span>
                        </div>
                      )}
                      {msg.role !== "system" && (
                        <div className={`max-w-[80%] px-3 py-2 rounded-xl text-xs ${
                          msg.role === "user" ? "bg-amber-500 text-black" : "bg-emerald-500/20 text-emerald-100"
                        }`}>
                          {msg.role === "admin" && <p className="text-[10px] font-bold text-emerald-400 mb-0.5">{msg.sender || "Agent"}</p>}
                          <p>{msg.content}</p>
                        </div>
                      )}
                      {msg.role === "user" && (
                        <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center flex-shrink-0">
                          <User className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={liveEndRef} />
                </div>
                <div className="flex gap-2">
                  <Input
                    value={liveInput}
                    onChange={e => setLiveInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendLiveMessage())}
                    placeholder="Type a message to agent..."
                    className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-zinc-500 rounded-xl text-sm focus:border-emerald-500/50"
                    data-testid="live-agent-input"
                  />
                  <Button onClick={sendLiveMessage} disabled={!liveInput.trim()} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl px-3" data-testid="live-agent-send">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <button onClick={closeLiveSession} className="text-zinc-500 text-[10px] mt-2 hover:text-zinc-300 underline w-full text-center">
                  End live chat & return to AI support
                </button>
              </div>
            ) : (
              /* AI Chat Input + Talk to Human */
              <div>
                {liveHours.closed && (
                  <div className="mb-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2" data-testid="live-agent-closed-banner">
                    <p className="text-amber-200 text-[11px] leading-tight">
                      <span className="font-semibold">Live agents are offline for the weekend.</span> Human support reopens <span className="font-semibold">{liveHours.next_open}</span>. Ask below — the AI can help you now and will leave a note for our team if it can&apos;t.
                    </p>
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-zinc-500 rounded-xl focus:border-amber-500/50"
                    disabled={isLoading}
                    data-testid="support-chat-input"
                  />
                  <Button
                    onClick={() => sendMessage()}
                    disabled={!inputValue.trim() || isLoading}
                    className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl px-4"
                    data-testid="support-chat-send"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-zinc-600 text-[10px] text-center mt-2">
                  Powered by AI • 24/7 Support
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SupportChatWidget;
